<template>
  <PriorHeader />
  <v-alert
    v-if="errorMsg"
    class="error-msg"
    :text="errorMsg"
    type="error"
    variant="outlined"
    :closable="true"></v-alert>
  <div class="d-flex flex-column align-center mt-8">
    <v-container :fluid="true" class="lh--container-wide">
      <v-alert
        v-if="!showLoader && insuranceCards !== undefined && insuranceCards.length === 0"
        text="This patient has no insurance cards available"
        type="error"
        variant="outlined"></v-alert>
      <h1 class="text-h4">Select Active Insurance for Prior Authorization</h1>
      <v-container :fluid="true" class="review-container">
        <v-row no-gutters>
          <v-col v-if="!showLoader" cols="8">
            <PatientBar
              v-if="patientInfo"
              :patient="patientInfo"
              :disabled="true"
              :client-company="intakeOrder?.clientCompany.id" />
          </v-col>
          <v-col v-if="!showLoader" cols="4">
            <ClassificationBar />
          </v-col>
          <v-container v-if="showLoader">
            <v-skeleton-loader type="text@4" />
          </v-container>
        </v-row>
        <PagePreview
          v-if="currentDocumentPage && !showLoader && requirementsStore.clientConfig"
          ref="previewRef"
          :insurance-cards="insuranceCards"
          :active-insurance-card-id="activeInsuranceCardId"
          @attribute-edit="onAttributeEdit"
          @select-insurance-card="onSelectInsuranceCard"
          @delete-content="onDeleteContent"
          @snooze="onSnooze"
          @archive="onArchive" />
        <v-skeleton-loader v-if="showLoader || currentDocumentPage === null" type="image@8" />
        <div class="document-footer">
          <v-skeleton-loader :loading="showLoader" type="heading">
            <v-row>
              <v-col>
                <v-btn-lh @click="goBack">Go Back</v-btn-lh>
              </v-col>
              <v-spacer></v-spacer>
              <v-col v-if="insuranceCards.length > 0" align="right">
                <v-btn-lh
                  v-if="skipClinicalReview"
                  :disabled="!activeInsuranceCardId || buttonDisabled"
                  :loading="showLoader"
                  @click="skipToPriorAuth">
                  Start Prior Authorization
                </v-btn-lh>
                <v-btn-lh v-else :disabled="!activeInsuranceCardId" @click="setCheckPayorInNetwork">
                  Check In-Network Status
                </v-btn-lh>
              </v-col>
            </v-row>
          </v-skeleton-loader>
        </div>
      </v-container>
      <div v-if="checkPayorInNetwork && !showLoader" class="qualify-form">
        <form @submit.prevent="">
          <div class="header">
            Qualify Patient
            <span v-if="intakeOrder?.insuranceCard" :style="{ color: payorInNetwork ? 'green' : 'red' }">
              {{ payorInNetwork ? "Payor in network" : "Payor not in network" }}
            </span>
          </div>
          <hr />
          <br />
          <span class="text-h6 text-primary">
            An insurance card has been selected. If you wish to proceed please continue to document review or select
            "No" to archive the order.
          </span>
          <br />
          <br />
          <v-select
            v-model="continueProcessing"
            :items="['Yes', 'No']"
            label="Do you want to continue processing this patient?" />
          <br />
          <v-select
            v-if="continueProcessing === 'No'"
            v-model="notifyProvider"
            :items="['Yes', 'No']"
            label="Do you want to notify the referring provider if you will not be processing this patient?" />
          <div v-if="notifyProvider === 'Yes' && continueProcessing === 'No'" class="mt-4">
            <label for="qualify-patient-template"> If yes, please update notification template: </label>
            <OutgoingFaxEditor
              v-if="intakeOrder"
              title=""
              :types="[TEMPLATE_TYPES.INTAKE_DISCONTINUE_PROCESSING]"
              :cancel-button="false"
              send-button-text="Send Fax and Archive Order"
              :intake-order="intakeOrder"
              :to-fax-number="intakeOrder.orderForm?.providerFax"
              @on-complete="onNotifyProviderComplete" />
          </div>
        </form>
      </div>
      <div v-if="checkPayorInNetwork && !showLoader">
        <v-container class="pa-0 pb-8">
          <v-row no-gutters>
            <v-col>
              <v-btn-lh @click="goBack">Go Back</v-btn-lh>
            </v-col>
            <v-col>
              <v-btn-lh v-if="continueProcessing === 'Yes'" class="float-right" @click="submitStep">
                Continue to Document Review
              </v-btn-lh>
              <v-btn-lh v-if="continueProcessing === 'No'" class="float-right" @click="onArchive">
                Archive Order
              </v-btn-lh>
            </v-col>
          </v-row>
        </v-container>
      </div>
      <AuditEntryList v-if="intakeOrder" :intake-order-id="intakeOrder.id" />
    </v-container>
  </div>
  <v-dialog v-model="archiveReasonDialog" max-width="800">
    <ArchiveNote :intake-order="intakeOrder" @on-archived="onArchived" />
  </v-dialog>
  <PriorFooter />
</template>

<script setup>
// Core imports
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useDate, useGoTo } from "vuetify";

// Component imports
import PriorFooter from "@/components/PriorFooter.vue";
import PriorHeader from "@/components/PriorHeader.vue";
import PagePreview from "@/components/intakeReview/PagePreview.vue";
import PatientBar from "@/components/intakeReview/PatientBar.vue";
import ClassificationBar from "@/components/intakeReview/ClassificationBar.vue";
import AuditEntryList from "@/components/AuditLogList.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import ArchiveNote from "@/components/ArchiveNote.vue";
import { useIntakeActions } from "@/composables/useIntakeActions";

// Service and model imports
import { INTAKE_STAGE, IntakeInsuranceCard, IntakeOrder, Patient } from "@/models/intake";
import { intakeService } from "@/services/intakeService";
import router from "@/router";
import { useIntakeStore } from "@/stores/intakeStore";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { TEMPLATE_TYPES } from "@/models/templates";
import { handleFormValidationErrors } from "@/utils/validators";

const intakeActions = useIntakeActions();
const requirementsStore = useRequirementsStore();
const intakeStore = useIntakeStore();
const dateAdapter = useDate();
const showLoader = ref(true);
const previewRef = ref(null);
const intakeOrder = computed(() => intakeStore.intakeOrder);
const insuranceCards = computed(() => intakeStore.insuranceCards);
const currentDocumentPage = ref(null);
const activeInsuranceCardId = ref(null);
const pageChanger = intakeStore.pageManager;
const errorMsg = ref(null);
const patientInfo = ref(null);
const payorInNetwork = ref(false);
const continueProcessing = ref("Yes");
const notifyProvider = ref("No");
const checkPayorInNetwork = ref(false);
const buttonDisabled = ref(false);
const skipClinicalReview = computed(() => requirementsStore.clientConfig?.intake?.skipClinicalReview ?? false);
const archiveReasonDialog = ref(false);

const route = useRoute();
const scrollTo = useGoTo();

onMounted(async () => {
  intakeStore.reset();
  intakeStore.stage = INTAKE_STAGE.QUALIFY_PATIENT;
  await fetchIntakeOrder();
  await requirementsStore.refreshClientConfig(intakeOrder.value.clientCompany.id);
});

window.addEventListener("beforeunload", () => {
  // Fire and forget the save updates
  insuranceCards.value?.map((card) => card.saveUpdates());
  intakeOrder.value?.saveUpdates();
});

const markAsActiveInsurance = async () => {
  errorMsg.value = "";
  if (
    requirementsStore.clientConfig?.submissionMethod === "cover_my_meds" &&
    insuranceCards.value[pageChanger.currentPageIndex].benefitType !== "PHARMACY"
  ) {
    errorMsg.value =
      "Payor portal requires pharmacy benefit type for prescription claims. Please select a pharmacy card.";
  } else {
    try {
      const order = await intakeService.patchIntakeOrder(intakeOrder.value.id, {
        insuranceCardId: insuranceCards.value[pageChanger.currentPageIndex].id,
      });
      intakeStore.intakeOrder = new IntakeOrder(order, dateAdapter);
      activeInsuranceCardId.value = intakeStore.intakeOrder.insuranceCard?.id;
      updatePayorInNetwork();
    } catch (e) {
      errorMsg.value = "Failed to mark insurance card as active";
    }
  }
};

const updatePayorInNetwork = () => {
  const card = insuranceCards.value.find((card) => card.id === activeInsuranceCardId.value);
  payorInNetwork.value = (intakeOrder.value.payorNames ?? []).indexOf(card?.payorName) !== -1;
};

const onSelectInsuranceCard = async (cardId) => {
  pageChanger.goToDataFormPage(insuranceCards.value.find((card) => card.id === cardId));
  checkPayorInNetwork.value = false;
  await markAsActiveInsurance();
};

const onAttributeEdit = (field, value, type, id) => {
  const card = insuranceCards.value.find((card) => card.id === id);
  card.updateAttribute(field.key, value, type);
  card.debouncedSaveUpdates();
  if (field.key === "payorName") {
    updatePayorInNetwork();
  }
};

const onDeleteContent = async (content, documentType) => {
  if (!(await saveAnyPendingData())) {
    return;
  }
  errorWrapper(async () => {
    intakeActions.onDeleteContent(content, documentType);
    await fetchIntakeOrder();
  }, "Error deleting the content");
};

const saveAnyPendingData = async () => {
  return await errorWrapper(async () => {
    await Promise.all([...insuranceCards.value.map((card) => card.saveUpdates()), intakeOrder.value.saveUpdates()]);
  }, "Error saving any pending data");
};

const setCheckPayorInNetwork = () => {
  checkPayorInNetwork.value = true;
  scrollTo(".document-footer", { behavior: "smooth", duration: 1000 });
};

const skipToPriorAuth = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  buttonDisabled.value = true;
  if (
    requirementsStore.clientConfig?.submissionMethod === "cover_my_meds" &&
    intakeOrder.value.insuranceCard?.benefitType !== "PHARMACY"
  ) {
    errorMsg.value =
      "Payor portal requires pharmacy benefit type for prescription claims. Please select a pharmacy card.";
    showLoader.value = false;
    buttonDisabled.value = false;
    return;
  }
  if (previewRef.value.formErrors.length > 0) {
    errorMsg.value = "There are errors in the insurance card. Please make sure that all required fields are filled out";
    showLoader.value = false;
    buttonDisabled.value = false;
    return;
  }
  try {
    // Submit the order to clinical review
    await Promise.all(insuranceCards.value.map((card) => card.saveUpdates()));
    await intakeOrder.value.saveUpdates();
    await intakeService.postOrdersSubmitClinicalReview(intakeOrder.value.id);
    // Redirect to kanban board, depending on submission method (i.e. CoverMyMeds) some orders will
    // require standby time before status will switch from Created to Requires Review
    router.push({ name: "all-patients" });
  } catch (ex) {
    errorMsg.value = `Error submitting the review: ${ex.message}`;
  } finally {
    showLoader.value = false;
    buttonDisabled.value = false;
  }
};

const submitStep = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  const isValid = await handleFormValidationErrors(previewRef.value.formErrors);
  if (!isValid) {
    errorMsg.value = "There are some errors with the document data";
    showLoader.value = false;
    return;
  }
  if (!intakeOrder.value.insuranceCard) {
    errorMsg.value = "No active insurance card selected";
    showLoader.value = false;
    return;
  }
  if (
    requirementsStore.clientConfig?.submissionMethod === "cover_my_meds" &&
    intakeOrder.value.insuranceCard?.benefitType !== "PHARMACY"
  ) {
    errorMsg.value =
      "Payor portal requires pharmacy benefit type for prescription claims. Please select a pharmacy card.";
    showLoader.value = false;
    return;
  }
  try {
    // Submit the order to clinical review
    await Promise.all(insuranceCards.value.map((card) => card.saveUpdates()));
    await intakeService.postOrderSubmitQualify(intakeOrder.value.id);
    router.push({ name: "document-review-order", params: { id: intakeOrder.value.id } });
  } catch (e) {
    errorMsg.value = "Failed to submit the Draft Order";
  }
  showLoader.value = false;
};

const onNotifyProviderComplete = ({ sent }) => {
  if (sent !== null) {
    onArchive();
  }
};

const fetchPatientQualifyDocuments = async (patientId) => {
  errorMsg.value = "";
  try {
    const result = await intakeService.getPatientInsuranceCards(patientId);
    intakeStore.insuranceCards = result.results.map((r) => new IntakeInsuranceCard(r, dateAdapter));
    currentDocumentPage.value = intakeStore.insuranceCards[0]?.pages[0];
    pageChanger.pages = intakeStore.insuranceCards.map((card) => card.pages[0]);
  } catch (e) {
    errorMsg.value = `Failed to fetch patient insurance cards: ${e.message || e}`;
  }
};

const fetchIntakeOrder = async () => {
  errorMsg.value = "";
  try {
    const result = await intakeService.getIntakeOrder(route.params.id);
    intakeStore.intakeOrder = new IntakeOrder(result, dateAdapter);
    patientInfo.value = new Patient(result.patient, dateAdapter);
    const patientId = intakeOrder.value.patient.id;
    activeInsuranceCardId.value = intakeOrder.value.insuranceCard?.id;
    await fetchPatientQualifyDocuments(patientId);
    updatePayorInNetwork();
  } catch (e) {
    errorMsg.value = `Error fetching the Draft Order: ${e.message || e}`;
  } finally {
    showLoader.value = false;
  }
};

const goBack = () => {
  router.push({ name: "all-patients" });
};

const errorWrapper = async (fn, errorPrefix) => {
  showLoader.value = true;
  try {
    await fn();
    return true;
  } catch (e) {
    errorMsg.value = `${errorPrefix} ${e.message}`;
  } finally {
    showLoader.value = false;
  }
  return false;
};

const onSnooze = async ({ minutes }) => {
  await errorWrapper(async () => {
    intakeStore.intakeOrder = await intakeActions.onSnooze({ orderId: intakeOrder.value.id, minutes });
  }, "Error snoozing the order");
};

const onArchive = async () => {
  if (intakeOrder.value.status === INTAKE_STAGE.ARCHIVED) {
    try {
      showLoader.value = true;
      await intakeService.deleteArchiveOrder(intakeOrder.value.id);
    } catch (e) {
      errorMsg.value = `Error unarchiving the order: ${e.message}`;
    } finally {
      showLoader.value = false;
      archiveReasonDialog.value = false;
    }
  } else {
    archiveReasonDialog.value = true;
  }
};

const onArchived = () => {
  archiveReasonDialog.value = false;
  router.push({ name: "all-patients" });
};
</script>

<!-- TODO: split css files -->
<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";
</style>
